import type { ToggleEntry } from '@warp-ds/react/components/toggle';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { ResultPageLayout } from 'shared/page/result/ResultPageLayout';
import { SearchPanel } from 'shared/page/result/SearchPanel';
import { Sorting } from 'shared/page/result/Sorting';
import { DestinationFilter } from '@/components/filters/DestinationFilter';
import { FilterGroup } from '@/components/filters/FilterGroup';
import { SmallGeoMap } from '@/components/map/SmallGeoMap';
import { SearchDetails } from '@/components/search/SearchDetails';
import { SearchForm } from '@/components/search/SearchForm';
import { useUnleash } from '@/hooks/unleash';
import type { PaleState, ThunkDispatchFunc } from '@/interfaces';
import { filtersToBrowserQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';
import { trackDestinationFilterClick } from '@/lib/utils/tracking';
import displayValues from '@/resources/displayValues.json';
import filterActions from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';
import CurrentFilters from './filters/currentFilters/CurrentFilters';
import type { PopularDestination } from './filters/destinationFilterData';
import { Filters } from './filters/Filters';
import { ResultList } from './resultList/ResultList';

const sorting: Record<string, string> = displayValues.sorting;

export const ResultPage = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const unleash = useUnleash();

    const [showSearchForm, setShowSearchForm] = useState<boolean>(false);

    const filters = useSelector((state: PaleState) => state.filters);
    const numberOfOffers = useSelector((state: PaleState) => state.results.numberOfHotels);
    const sortOptions = useSelector((state: PaleState) => state.results.sortOptions);
    const defaultSort = useSelector((state: PaleState) => state.results.defaultSort);
    const selectedSort = useSelector((state: PaleState) => state.filters.sort);

    const validSortOptions = (sortOptions ?? []).filter((option) => Boolean(sorting[option]));
    const sortToggleOptions: ToggleEntry[] = validSortOptions.map((option) => ({
        label: sorting[option],
        value: option,
    }));

    const mapUrl = useMemo(() => {
        const queryString = stringifyParams(filtersToBrowserQueryParams(filters));
        return queryString ? `/reise/pakkereiser/kart?${queryString}` : '/reise/pakkereiser/kart';
    }, [filters]);

    const onSortChange = useCallback(
        (sort: string | undefined): void => {
            dispatch(filterActions.changeSort(sort));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    const handleFilterChange = useCallback(() => {
        dispatch(fetchResults());
    }, [dispatch]);

    const handleDestinationChange = useCallback(
        (dest: PopularDestination) => {
            const isSelected = filters.destinations.some((d) => d.label === dest.label);
            trackDestinationFilterClick(dest.label, isSelected);
            if (isSelected) {
                dispatch(filterActions.setDestinations(filters.destinations.filter((d) => d.label !== dest.label)));
            } else {
                dispatch(
                    filterActions.setDestinations([
                        ...filters.destinations,
                        { label: dest.label, boundingBox: dest.boundingBox, country: dest.country },
                    ]),
                );
            }
            dispatch(fetchResults());
        },
        [dispatch, filters.destinations],
    );

    return (
        <>
            <SimpleHead
                title="Pakkereiser - sammenlign pris | FINN reise"
                canonical="https://www.finn.no/reise/pakkereiser/resultat"
                description="Finn billige pakkereiser fra alle reisebyrå på ett sted! På FINN.no kan du sammenligne priser og spare penger."
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                <ResultPageLayout
                    title="Pakkereiser"
                    breadcrumbs={{ path: 'pakkereiser' }}
                    search={
                        <SearchPanel
                            showSearchForm={showSearchForm}
                            setShowSearchForm={setShowSearchForm}
                            searchForm={<SearchForm onSubmit={() => setShowSearchForm(false)} />}
                            searchDetails={<SearchDetails />}
                        />
                    }
                    filters={
                        <>
                            <div className="hidden md:block">
                                <SmallGeoMap />
                            </div>
                            {!unleash?.geoHierarchyDestinationPicker && (
                                <FilterGroup title="Populære reisemål" testId="destinationFilter">
                                    <DestinationFilter
                                        selectedDestinationLabels={filters.destinations.map((d) => d.label)}
                                        onDestinationChange={handleDestinationChange}
                                    />
                                </FilterGroup>
                            )}
                            <Filters onFilterChange={handleFilterChange} />
                        </>
                    }
                    sortingSection="pakkereiser"
                    sorting={
                        <Sorting
                            sortOptions={sortToggleOptions}
                            defaultSort={defaultSort}
                            selectedSort={selectedSort}
                            onChange={onSortChange}
                        />
                    }
                    mapUrl={mapUrl}
                    numberOfOffers={numberOfOffers}>
                    <CurrentFilters />
                    <ResultList pageType="List" />
                </ResultPageLayout>
            </Main>
        </>
    );
};
